import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import "../../assets/css/--component-notification-history.css";
import {
  formatDate,
  formatSmartTime,
  getCustomerNotification,
  getDateGroup,
  removeCustomerNotification,
  updateCustomerNotification,
} from "../../helper/backend-methods";
import ConfirmationModal from "../../components/StatusModal";
import noNotification from "../../assets/images/no-notifications.png";
import userImg from "../../assets/images/delivery.png";
import { useNavigate } from "react-router-dom";
import AppLoader from "../../components/AppLoader";
import { onNotification } from "../../helper/signalr-service";

const NotificationHistory = () => {
  const [showModal, setShowModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState({
    title: "Confirmation",
    message: "Are you sure you want to clear all notifications?",
  });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const templateMap = {
    1: {
      type: "order",
      icon: "ri-time-fill",
      title: "Order Update",
      color: "blue",
    },
    4: {
      type: "order",
      icon: "ri-check-fill",
      title: "Order Ready",
      color: "green",
    },
    5: {
      type: "warning",
      icon: "ri-error-warning-fill",
      title: "Order Cancelled",
      color: "red",
    },
    6: {
      type: "offer",
      icon: "ri-gift-fill",
      title: "Promotion",
      color: "yellow",
    },
  };
  const history = useNavigate();

  // Group notifications by date
  const groupedNotifications = notifications.reduce((groups, notification) => {
    const group = notification.dateGroup;
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(notification);
    return groups;
  }, {});

  const groupLabels = {
    today: "Today",
    yesterday: "Yesterday",
    earlier: "Earlier",
  };

  const groupColors = {
    today: "grey-bg",
    yesterday: "white-bg",
  };

  onNotification((response) => {
    getAllNotifications(); // or toast
  });

  const getAllNotifications = () => {
    getCustomerNotification({ status: "All", limit: 0 })
      .then(({ data }) => {
        setLoading(true);
        if (data.Status) {
          let notifications = data.Data.sort((a, b) => b.Id - a.Id);
          const _notifications = notifications.map((item) => {
            const meta = templateMap[item.NotificationTemplateId] || {};

            return {
              id: item.Id,
              type: meta.type || "info",
              title: meta.title || "Notification",
              message: item.Message,
              time: formatSmartTime(formatDate(item.CreatedOnUtc)),
              icon: meta.icon || "ri-notification-3-fill",
              unread: !item.IsRead,
              dateGroup: getDateGroup(item.CreatedOnUtc),
              color: meta.color || "blue",
            };
          });
          setNotifications(_notifications);
        }
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
      });
  };

  const NotificationItem = ({ item }) => (
    <div className="notification-item" onClick={() => updateCustomerNotificationAsRead(item.id)}>
      <Row className="noti-row align-items-start">
        <Col xs="auto" className="pe-0">
          <div className={`notification-icon ${item.color}`}>
            <img src={userImg} alt="notification" />
            {item.unread && <span className="unread-indicator"></span>}
          </div>
        </Col>

        <Col className="notification-content-col">
          <div className="notification-content-wrapper">
            <div
              className="noti-message"
              dangerouslySetInnerHTML={{
                __html: item.message,
              }}
            />
          </div>
        </Col>

        <Col xs="auto" className="time-col ps-0">
          <span className="noti-time">{item.time}</span>
        </Col>
      </Row>
    </div>
  );

  const clearAllNotifications = () => {
    removeCustomerNotification().then(({ data }) => {
      if (data.Status) {
        setShowModal(false);
        getAllNotifications();
      }
    });
  };

  const handleCancelSubmit = () => {
    setShowModal(false);
  };

  const updateCustomerNotificationAsRead = (notificationId) => {
    updateCustomerNotification("?NotificationId=" + notificationId).then(({ data }) => {
      if (data.Status) {
        getAllNotifications();
        history("/account/current-orders");
      }
    });
  };

  const handleRefresh = () => {
    getAllNotifications();
  };

  useEffect(() => {
    getAllNotifications();
  }, []);

  return (
    <section id="notification-history" className="mt-5 ms-4">
      <div className="d-flex justify-content-between align-items-center mb-3 grill-title-box">
        <h1 className="fs-30 text-blakish fw-600 mt-0 mb-0 main-title">Notifications</h1>
        <button className="clear-btn" onClick={() => setShowModal(true)} disabled={notifications.length === 0}>
          CLEAR ALL <span className="flex-btn">NOTIFICATIONS</span>
        </button>
      </div>
      <span className="separator my-3" />

      <div className="notification-listing-container">
        <div className="history-list">
          {notifications.length ? (
            Object.entries(groupedNotifications).map(([groupKey, groupItems]) => (
              <React.Fragment key={groupKey}>
                <h6 className="group-title">{groupLabels[groupKey] || groupKey}</h6>

                <div className={`notification-group ${groupColors[groupKey] || ""}`}>
                  {groupItems.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <NotificationItem item={item} />
                      {index !== groupItems.length - 1 && <div className="notification-divider"></div>}
                    </React.Fragment>
                  ))}
                </div>
              </React.Fragment>
            ))
          ) : loading ? (
            <div className="loader-center">
              <AppLoader />
            </div>
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center flex-wrap text-center mt-5 pt-5">
              <img src={noNotification} alt="No notifications" />
              <h2 className="fs-36 fw-600 text-blakish mt-4">No Notifications Yet</h2>
              <p className="fs-18 fw-600 text-blakish mt-2">You're all caught up! Any important updates will appear here.</p>
              <button className="explore-menu-btn mt-3 d-flex align-items-center gap-1" onClick={handleRefresh}>
                <i className="ri-loop-left-line"></i>
                REFRESH
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={showModal}
        toggle={() => setShowModal(false)}
        type="confirmation"
        title={confirmationMessage.title}
        message={confirmationMessage.message}
        ButtonText="No"
        ButtonTextYes="Yes"
        onCancel={handleCancelSubmit}
        onYes={clearAllNotifications}
        isShowYesButton={true}
      />
    </section>
  );
};

export default NotificationHistory;
